import { useState, type MouseEvent, type ReactNode } from 'react'
import Box from '@mui/joy/Box'
import Button, { type ButtonProps } from '@mui/joy/Button'
import Typography from '@mui/joy/Typography'
import { useSetAtom } from 'jotai'

import { useCheckoutReservationHandler } from '@/hooks/use-checkout-reservation-handler'
import { ReservationConfirmationModal } from '@/pwa/components/reservation-confirmation-modal'
import { suggestedStartTimeShiftAtom } from '@/state'

type CheckoutButtonProps = Omit<ButtonProps, 'children' | 'onClick'> & {
    garageId: number | string | undefined
    label: ReactNode
    popupText: string
    showPopup: boolean
    onSuccess: () => void | Promise<void>
    stopPropagation?: boolean
}

export default function CheckoutButton({
    garageId,
    label,
    popupText,
    showPopup,
    onSuccess,
    stopPropagation = false,
    loading: loadingProp = false,
    ...buttonProps
}: CheckoutButtonProps) {
    const [open, setOpen] = useState(false)
    const setSuggestedStartTimeShift = useSetAtom(suggestedStartTimeShiftAtom)
    const {
        errorMessage,
        loading,
        handleReserveAndCheckout,
    } = useCheckoutReservationHandler({
        garageId,
        onSuccess,
    })

    const handleClose = () => {
        setOpen(false)
    }

    const handleClick = (event: MouseEvent<HTMLButtonElement>) => {
        if (stopPropagation) {
            event.stopPropagation()
        }

        setSuggestedStartTimeShift(null)

        if (showPopup) {
            setOpen(true)
            return
        }

        void handleReserveAndCheckout()
    }

    const handleConfirm = async () => {
        await handleReserveAndCheckout()
    }

    return (
        <>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'stretch', gap: 1 }}>
                <Button
                    {...buttonProps}
                    onClick={handleClick}
                    loading={loading || loadingProp}
                >
                    {label}
                </Button>
                {errorMessage &&
                    <Typography
                        level="body-sm"
                        sx={{
                            color: 'danger.500',
                            textAlign: 'center',
                        }}
                    >
                        {errorMessage}
                    </Typography>
                }
            </Box>
            {showPopup &&
                <ReservationConfirmationModal
                    popupText={popupText}
                    open={open}
                    handleClose={handleClose}
                    handleConfirm={handleConfirm}
                    loading={loading}
                    errorMessage={errorMessage}
                />
            }
        </>
    )
}
